// Task 10 — elevated cross-member buffer override warning. Shown when an ELEVATED user (admin/PM)
// attempts to create a booking and the REAL create request 409s with `conflictType: "buffer_overlap"`
// (a DIFFERENT member's buffer — never a real double-booking). The /can-book preflight never surfaces
// an overridable conflict, so this dialog only ever appears after a genuine submit attempt. Confirming
// resubmits the identical create payload plus `overrideBufferConflict: true` (backend Task 5 honours
// the flag for elevated callers only). Presentational only — the elevation gate and 409 detection live
// at the call site (MakeABookingDialogProvider.submitBooking).
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { destructiveButtonClasses, secondaryButtonClasses } from "@/constants/ui";

interface BufferOverrideDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** The backend's conflict message (validationResult.message from the 409 body). */
  conflictMessage: string;
  onConfirm: () => void;
  isLoading?: boolean;
}

export const BufferOverrideDialog = ({
  isOpen,
  onOpenChange,
  conflictMessage,
  onConfirm,
  isLoading = false,
}: BufferOverrideDialogProps) => {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-800">
            <AlertTriangle className="size-5 text-amber-600" />
            This butts against another member's buffer
          </DialogTitle>
          <DialogDescription>
            This normally isn't allowed — the adjacent booking belongs to a different member and will lose its
            buffer time if you proceed.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 text-sm">
          <p className="font-semibold text-amber-900 mb-1">Buffer conflict</p>
          <p className="text-amber-800">{conflictMessage}</p>
          <p className="text-amber-800 mt-2">
            As an elevated user you can override this and book anyway. This will be recorded in the activity trail.
          </p>
        </div>

        <DialogFooter className="flex-col items-stretch gap-2 sm:flex-col sm:items-stretch sm:gap-2 sm:space-x-0">
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirm}
            disabled={isLoading}
            className={destructiveButtonClasses}
            data-testid="buffer-override-confirm"
          >
            {isLoading ? "Booking..." : "Book anyway"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
            className={secondaryButtonClasses}
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BufferOverrideDialog;
